/**
 * TeamHub — single source of truth for adding/removing team members across
 * the platform. Shows:
 *   1. Studio team (platform-wide single team — operators, managers, admins)
 *   2. Campaign teams (per-campaign — pick a campaign, manage that campaign's
 *      chasers / managers separately)
 *
 * The Studio team is a SINGLE shared list because The Studio is a finite
 * resource (rooms + gear). Campaign teams are PER-CAMPAIGN because each
 * campaign is owned by a different cause/department.
 */
import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Users, Heart, Video, ArrowRight, Shield, Loader2, Info, ChevronRight,
  Plus, Trash2, Wrench,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';


const ROLE_DOTS = {
  campaign_admin:   'bg-violet-500',
  campaign_manager: 'bg-blue-500',
  donation_chaser:  'bg-emerald-500',
  viewer:           'bg-slate-400',
  studio_admin:     'bg-violet-500',
  studio_manager:   'bg-blue-500',
  studio_operator:  'bg-emerald-500',
};


const VALID_TABS = ['campaign', 'studio', 'pos', 'security', 'dining', 'cctv', 'housing', 'facilities'];

const TeamHub = () => {
  const [params, setParams] = useSearchParams();
  const tabParam = params.get('tab');
  const defaultTab = VALID_TABS.includes(tabParam) ? tabParam : 'campaign';
  return (
    <div className="min-h-screen bg-slate-50" data-testid="team-hub">
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Users className="h-6 w-6 text-emerald-600" />
            Team Management
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Add people to teams and assign their roles. Everyone here is scoped — they only see what you let them see.
          </p>
        </div>

        {/* Explainer card */}
        <Card className="mb-6 bg-emerald-50 border-emerald-200">
          <CardContent className="p-4 flex gap-3">
            <Info className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-slate-700 space-y-1.5">
              <p className="font-medium text-slate-800">How scoping works</p>
              <ul className="list-disc list-inside space-y-1 text-slate-600">
                <li><strong>Studio team</strong> = platform-wide. One shared team manages rooms & gear because The Studio is a single physical resource.</li>
                <li><strong>Campaign team</strong> = per-campaign. Each campaign has its own team — a chaser added to ABMHC <em>cannot see</em> any other campaign.</li>
                <li>A super admin always bypasses scope and sees everything.</li>
                <li>Adding a brand-new person creates their admin login automatically (you set their temporary password).</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        <Tabs
          value={defaultTab}
          onValueChange={(v) => setParams({ tab: v }, { replace: true })}
          className="w-full"
        >
          <TabsList className="grid w-full max-w-2xl grid-cols-3 sm:grid-cols-8">
            <TabsTrigger value="campaign" data-testid="team-hub-tab-campaign"><Heart className="h-3.5 w-3.5 mr-1" />Campaigns</TabsTrigger>
            <TabsTrigger value="studio" data-testid="team-hub-tab-studio"><Video className="h-3.5 w-3.5 mr-1" />Studio</TabsTrigger>
            <TabsTrigger value="pos" data-testid="team-hub-tab-pos">POS</TabsTrigger>
            <TabsTrigger value="security" data-testid="team-hub-tab-security">Security</TabsTrigger>
            <TabsTrigger value="dining" data-testid="team-hub-tab-dining">Dining</TabsTrigger>
            <TabsTrigger value="cctv" data-testid="team-hub-tab-cctv">CCTV</TabsTrigger>
            <TabsTrigger value="housing" data-testid="team-hub-tab-housing">Housing</TabsTrigger>
            <TabsTrigger value="facilities" data-testid="team-hub-tab-facilities"><Wrench className="h-3.5 w-3.5 mr-1" />Facilities</TabsTrigger>
          </TabsList>
          <TabsContent value="campaign" className="mt-4"><CampaignTeamsHub /></TabsContent>
          <TabsContent value="studio" className="mt-4"><StudioTeamHub /></TabsContent>
          <TabsContent value="pos" className="mt-4"><OpsTeamPanel system="pos" label="Point of Sale" /></TabsContent>
          <TabsContent value="security" className="mt-4"><OpsTeamPanel system="security" label="Security" /></TabsContent>
          <TabsContent value="dining" className="mt-4"><OpsTeamPanel system="dining" label="Dining" /></TabsContent>
          <TabsContent value="cctv" className="mt-4"><OpsTeamPanel system="cctv" label="CCTV" /></TabsContent>
          <TabsContent value="housing" className="mt-4"><OpsTeamPanel system="housing" label="Housing" /></TabsContent>
          <TabsContent value="facilities" className="mt-4"><OpsTeamPanel system="facilities" label="Facilities & Maintenance" /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
};


// ============= CAMPAIGN TEAMS =============
const CampaignTeamsHub = () => {
  const navigate = useNavigate();

  const { data: campaigns = [], isLoading } = useQuery({
    queryKey: ['admin-campaigns-list'],
    queryFn: () => api.get('/campaigns/admin/list').then(r => r.data),
  });

  if (isLoading) return <div className="py-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" /></div>;

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-500">
        Pick a campaign to manage its team. Each campaign maintains its own roster — adding a chaser to one campaign doesn't expose them to any other.
      </p>

      {campaigns.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-slate-400 text-sm">
            <Heart className="h-8 w-8 mx-auto mb-2 opacity-50" />
            No campaigns yet. Create one from <button className="underline" onClick={() => navigate('/admin/campaigns')}>Campaigns Admin</button> first.
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {campaigns.map(c => (
            <CampaignTeamCard key={c.id || c._id} campaign={c} />
          ))}
        </div>
      )}
    </div>
  );
};

const CampaignTeamCard = ({ campaign }) => {
  const navigate = useNavigate();
  const cid = campaign.id || campaign._id;
  const { data: members = [] } = useQuery({
    queryKey: ['campaign-team-count', cid],
    queryFn: () => api.get(`/campaigns/${cid}/team`).then(r => r.data),
    retry: false,
  });

  const goToCampaign = () => {
    // Campaign settings has the Team tab — open the campaigns admin and let
    // the operator click into the right campaign. (We don't have a deep-link
    // route to a specific campaign's team tab yet.)
    navigate('/admin/campaigns');
  };

  return (
    <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={goToCampaign} data-testid={`team-hub-campaign-${campaign.slug || cid}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-2 gap-2">
          <div className="flex-1 min-w-0">
            <p className="font-medium text-slate-900 truncate">{campaign.name}</p>
            <p className="text-xs text-slate-500 truncate">/{campaign.slug || '—'}</p>
          </div>
          <Badge variant="outline" className="text-xs">
            {members.length} member{members.length === 1 ? '' : 's'}
          </Badge>
        </div>
        {members.length > 0 && (
          <div className="flex items-center gap-1 mb-3 flex-wrap">
            {members.slice(0, 4).map(m => (
              <span
                key={m.id}
                className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-slate-100"
                title={`${m.email} — ${m.role}`}
              >
                <span className={`h-1.5 w-1.5 rounded-full ${ROLE_DOTS[m.role] || ROLE_DOTS.viewer}`} />
                <span className="truncate max-w-[80px]">{m.name || m.email.split('@')[0]}</span>
              </span>
            ))}
            {members.length > 4 && <span className="text-[10px] text-slate-400">+{members.length - 4}</span>}
          </div>
        )}
        <Button
          size="sm" variant="outline" className="w-full text-xs"
          data-testid={`team-hub-manage-${campaign.slug || cid}`}
        >
          Manage Team <ChevronRight className="h-3 w-3 ml-1" />
        </Button>
      </CardContent>
    </Card>
  );
};


// ============= STUDIO TEAM =============
const StudioTeamHub = () => {
  const navigate = useNavigate();

  const { data: members = [], isLoading, isError } = useQuery({
    queryKey: ['studio-team-hub'],
    queryFn: () => api.get('/studio/team').then(r => r.data),
    retry: false,
  });

  if (isLoading) return <div className="py-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <p className="text-sm text-slate-500 max-w-xl">
          One shared team for all rooms and gear. Roles control what each person can do, not which resource they can access.
        </p>
        <Button size="sm" onClick={() => navigate('/studio')} data-testid="team-hub-open-studio">
          Open Studio Portal <ArrowRight className="h-3.5 w-3.5 ml-1" />
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center justify-between">
            Studio Team
            <Badge variant="outline" className="text-xs">{members.length} member{members.length === 1 ? '' : 's'}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isError ? (
            <div className="py-8 text-center text-slate-400 text-sm">
              You don't have access to view the Studio team. Ask a Studio admin to add you first.
            </div>
          ) : members.length === 0 ? (
            <div className="py-8 text-center text-slate-400 text-sm">
              <Video className="h-6 w-6 mx-auto mb-2 opacity-50" />
              No members yet. Open the Studio portal to add the first one.
            </div>
          ) : (
            <div className="divide-y">
              {members.map(m => (
                <div key={m.id} className="p-3 flex items-center gap-3" data-testid={`team-hub-studio-${m.id}`}>
                  <div className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-500">
                    <Shield className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{m.name || m.email}</p>
                    <p className="text-xs text-slate-500 truncate">{m.email}</p>
                  </div>
                  <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-slate-100">
                    <span className={`h-2 w-2 rounded-full ${ROLE_DOTS[m.role] || ROLE_DOTS.viewer}`} />
                    {m.role.replace('studio_', '').replace('_', ' ')}
                  </span>
                  {!m.is_active && <Badge variant="outline" className="text-[10px] bg-red-50 text-red-600 border-red-200">Disabled</Badge>}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};


export default TeamHub;


// ============= OPS TEAM PANEL (POS / Security / Dining / CCTV / Housing) =============
// Full inline team management — parity with Studio team. Add member,
// change role, toggle active, remove. Backed by /api/ops/{system}/team.
const OpsTeamPanel = ({ system, label }) => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);

  const { data: systems = [] } = useQuery({
    queryKey: ['ops-systems'],
    queryFn: () => api.get('/ops/systems').then(r => r.data).catch(() => []),
  });
  const sysCfg = systems.find(s => s.system === system);
  const roles = sysCfg?.roles || [];

  const { data: members = [], isLoading, isError } = useQuery({
    queryKey: ['ops-team-hub', system],
    queryFn: () => api.get(`/ops/${system}/team`).then(r => r.data),
    retry: false,
  });

  const delMut = useMutation({
    mutationFn: (id) => api.delete(`/ops/${system}/team/${id}`),
    onSuccess: () => { toast.success('Removed'); qc.invalidateQueries({ queryKey: ['ops-team-hub', system] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Failed'),
  });
  const updateMut = useMutation({
    mutationFn: ({ id, payload }) => api.put(`/ops/${system}/team/${id}`, payload),
    onSuccess: () => { toast.success('Updated'); qc.invalidateQueries({ queryKey: ['ops-team-hub', system] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Failed'),
  });

  return (
    <div className="space-y-4" data-testid={`ops-team-panel-${system}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <p className="text-sm text-slate-500 max-w-xl">
          Platform-wide {label} team. Anyone added here can sign in at <span className="font-mono text-slate-700">/{system}/login</span>.
        </p>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => navigate(`/${system}`)} data-testid={`ops-team-open-${system}`}>
            Open {label} <ArrowRight className="h-3.5 w-3.5 ml-1" />
          </Button>
          <Button size="sm" onClick={() => setAdding(true)} data-testid={`ops-team-add-${system}`}>
            <Plus className="h-3.5 w-3.5 mr-1" />Add member
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center justify-between">
            {label} Team
            <Badge variant="outline" className="text-xs">{members.length} member{members.length === 1 ? '' : 's'}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="py-8 text-center"><Loader2 className="h-5 w-5 animate-spin inline text-slate-400" /></div>
          ) : isError ? (
            <div className="py-8 text-center text-slate-400 text-sm">
              You don't have access to view the {label} team. Ask a super admin or {label} manager to add you first.
            </div>
          ) : members.length === 0 ? (
            <div className="py-8 text-center text-slate-400 text-sm">
              <Users className="h-6 w-6 mx-auto mb-2 opacity-50" />
              No members yet. Click <strong>Add member</strong> to invite your first.
            </div>
          ) : (
            <div className="divide-y">
              {members.map(m => (
                <div key={m.id} className="p-3 flex items-center gap-3 flex-wrap" data-testid={`ops-team-row-${system}-${m.id}`}>
                  <div className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-500">
                    <Shield className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{m.name || m.email}</p>
                    <p className="text-xs text-slate-500 truncate">{m.email}</p>
                  </div>
                  <Select
                    value={m.role}
                    onValueChange={(v) => updateMut.mutate({ id: m.id, payload: { role: v, is_active: m.is_active } })}
                  >
                    <SelectTrigger className="h-8 w-full max-w-[180px] text-xs" data-testid={`ops-team-role-${m.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {roles.map(r => <SelectItem key={r} value={r} className="capitalize">{r.replace(/_/g, ' ')}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button
                    size="sm" variant="outline" className={`h-7 text-xs ${m.is_active ? 'text-slate-600' : 'text-amber-700 border-amber-200 bg-amber-50'}`}
                    onClick={() => updateMut.mutate({ id: m.id, payload: { role: m.role, is_active: !m.is_active } })}
                    data-testid={`ops-team-toggle-${m.id}`}
                  >
                    {m.is_active ? 'Active' : 'Disabled'}
                  </Button>
                  <Button
                    size="icon" variant="ghost" className="h-7 w-7 text-rose-600"
                    onClick={() => { if (window.confirm(`Remove ${m.email}?`)) delMut.mutate(m.id); }}
                    data-testid={`ops-team-remove-${m.id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      {adding && <OpsAddMemberDialog system={system} label={label} roles={roles} onClose={() => setAdding(false)} onAdded={() => { qc.invalidateQueries({ queryKey: ['ops-team-hub', system] }); setAdding(false); }} />}
    </div>
  );
};

const OpsAddMemberDialog = ({ system, label, roles, onClose, onAdded }) => {
  const [form, setForm] = useState({ email: '', name: '', role: roles[0] || '', password: '' });
  const mut = useMutation({
    mutationFn: () => api.post(`/ops/${system}/team`, form).then(r => r.data),
    onSuccess: () => { toast.success(`Added to ${label} team`); onAdded?.(); },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Failed'),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid={`ops-team-add-dialog-${system}`}>
        <DialogHeader>
          <DialogTitle>Add to {label} team</DialogTitle>
          <DialogDescription>New users get a starter password they can change after first login.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>Email *</Label><Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="ops-team-add-email" /></div>
          <div><Label>Name</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></div>
          <div>
            <Label>Role *</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger data-testid="ops-team-add-role"><SelectValue placeholder="Pick role" /></SelectTrigger>
              <SelectContent>{roles.map(r => <SelectItem key={r} value={r} className="capitalize">{r.replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Starter password <span className="text-xs text-slate-500 font-normal">(only for brand-new admins)</span></Label><Input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder="Leave blank if existing admin" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => { if (!form.email || !form.role) return toast.error('Email & role required'); mut.mutate(); }} disabled={mut.isPending} data-testid="ops-team-add-save">
            {mut.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Add
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
